import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Plus, Pencil, Trash2, FileText, Users, ExternalLink, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { COMPANY } from "@/lib/config";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  title: "", excerpt: "", content: "", cover_image: "", category: "Marketing",
  author: "Digitify Team", read_time: 5, tags: "", published: true,
};

const inputCls =
  "w-full rounded-xl bg-brand-slate/60 border border-white/10 px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors";

function PostDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({ ...editing, tags: (editing.tags || []).join(", ") });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.content) {
      toast.error("Title and content are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      read_time: Number(form.read_time) || 5,
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
    };
    try {
      if (editing) {
        await api.put(`/admin/blog/${editing.id}`, payload);
        toast.success("Article updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Article published");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save article");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-brand-navy border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{editing ? "Edit article" : "New article"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3" data-testid="post-form">
          <input className={inputCls} placeholder="Title" value={form.title} onChange={upd("title")} data-testid="post-title" />
          <textarea className={inputCls + " resize-none"} rows={2} placeholder="Excerpt (short summary)" value={form.excerpt} onChange={upd("excerpt")} data-testid="post-excerpt" />
          <textarea className={inputCls + " resize-none font-mono text-sm"} rows={10} placeholder="Content (Markdown supported: ## Heading, **bold**, - lists, > quotes)" value={form.content} onChange={upd("content")} data-testid="post-content" />
          <input className={inputCls} placeholder="Cover image URL" value={form.cover_image} onChange={upd("cover_image")} data-testid="post-cover" />
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Category" value={form.category} onChange={upd("category")} data-testid="post-category" />
            <input className={inputCls} placeholder="Author" value={form.author} onChange={upd("author")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} type="number" placeholder="Read time (min)" value={form.read_time} onChange={upd("read_time")} />
            <input className={inputCls} placeholder="Tags (comma separated)" value={form.tags} onChange={upd("tags")} />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="post-published" />
            <span className="text-sm text-slate-300">{form.published ? "Published" : "Draft"}</span>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="post-save" className="rounded-full bg-brand-blue hover:bg-brand-blueHover px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {saving ? "Saving…" : editing ? "Update" : "Publish"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPosts = useCallback(() => {
    api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);
  const loadLeads = useCallback(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadPosts();
    loadLeads();
  }, [loadPosts, loadLeads]);

  const deletePost = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success("Article deleted");
      loadPosts();
    } catch {
      toast.error("Could not delete");
    }
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead removed");
      loadLeads();
    } catch {
      toast.error("Could not delete");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-brand-slate text-slate-100" data-testid="admin-page">
      {/* Top bar */}
      <header className="glass-nav sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="Digitify My Biz" className="h-8 w-8 object-contain" />
            <div>
              <p className="font-heading font-bold text-white leading-none">Admin Dashboard</p>
              <p className="text-xs text-slate-400 mt-0.5">{user?.email}</p>
            </div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-white/5">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="glass-panel rounded-2xl p-5">
            <FileText className="h-5 w-5 text-brand-cyan" />
            <p className="mt-3 font-heading font-extrabold text-3xl text-white">{posts.length}</p>
            <p className="text-sm text-slate-400">Articles</p>
          </div>
          <div className="glass-panel rounded-2xl p-5">
            <Users className="h-5 w-5 text-brand-cyan" />
            <p className="mt-3 font-heading font-extrabold text-3xl text-white">{leads.length}</p>
            <p className="text-sm text-slate-400">Leads captured</p>
          </div>
          <div className="glass-panel rounded-2xl p-5">
            <FileText className="h-5 w-5 text-brand-cyan" />
            <p className="mt-3 font-heading font-extrabold text-3xl text-white">{posts.filter((p) => p.published).length}</p>
            <p className="text-sm text-slate-400">Published</p>
          </div>
        </div>

        <Tabs defaultValue="blog">
          <TabsList className="bg-brand-navy/60 border border-white/10">
            <TabsTrigger value="blog" data-testid="tab-blog" className="data-[state=active]:bg-brand-blue data-[state=active]:text-white">Blog Posts</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads" className="data-[state=active]:bg-brand-blue data-[state=active]:text-white">Leads</TabsTrigger>
          </TabsList>

          {/* BLOG TAB */}
          <TabsContent value="blog" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-xl text-white">Manage Articles</h2>
              <button
                onClick={() => { setEditing(null); setDialogOpen(true); }}
                data-testid="new-post-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand-blue hover:bg-brand-blueHover px-5 py-2.5 text-sm font-semibold text-white transition-colors"
              >
                <Plus className="h-4 w-4" /> New post
              </button>
            </div>

            <div className="glass-panel rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-left">
                  <tr>
                    <th className="px-5 py-3 font-medium">Title</th>
                    <th className="px-5 py-3 font-medium hidden sm:table-cell">Category</th>
                    <th className="px-5 py-3 font-medium hidden md:table-cell">Status</th>
                    <th className="px-5 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No articles yet. Create your first one!</td></tr>
                  )}
                  {posts.map((p) => (
                    <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-post-${p.id}`}>
                      <td className="px-5 py-3 text-white font-medium max-w-xs truncate">{p.title}</td>
                      <td className="px-5 py-3 text-slate-400 hidden sm:table-cell">{p.category}</td>
                      <td className="px-5 py-3 hidden md:table-cell">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${p.published ? "bg-brand-whatsapp/15 text-brand-whatsapp" : "bg-white/10 text-slate-400"}`}>
                          {p.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-slate-400 hover:text-brand-cyan hover:bg-white/5" title="View"><ExternalLink className="h-4 w-4" /></a>
                          <button onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`edit-post-${p.id}`} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" title="Edit"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => deletePost(p.id)} data-testid={`delete-post-${p.id}`} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/5" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* LEADS TAB */}
          <TabsContent value="leads" className="mt-6">
            <h2 className="font-heading font-bold text-xl text-white mb-4">Captured Leads</h2>
            <div className="glass-panel rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-left">
                  <tr>
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium hidden sm:table-cell">Phone</th>
                    <th className="px-5 py-3 font-medium hidden lg:table-cell">Message</th>
                    <th className="px-5 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No leads yet.</td></tr>
                  )}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-lead-${l.id}`}>
                      <td className="px-5 py-3 text-white font-medium">{l.name}</td>
                      <td className="px-5 py-3 text-slate-300">{l.email}</td>
                      <td className="px-5 py-3 text-slate-400 hidden sm:table-cell">{l.phone || "—"}</td>
                      <td className="px-5 py-3 text-slate-400 hidden lg:table-cell max-w-xs truncate">{l.message || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => deleteLead(l.id)} data-testid={`delete-lead-${l.id}`} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <PostDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} onSaved={loadPosts} />
    </div>
  );
}
